/**
 * Neuro-Adaptive Learning Service
 * Analyzes student behavior patterns to optimize learning in real-time
 * Privacy-first: All processing happens locally, no video/audio sent to server
 */

class NeuroAdaptiveService {
  constructor() {
    this.isActive = false;
    this.metrics = {
      typingSpeed: [],
      pauseDurations: [],
      errorPatterns: [],
      focusScore: 0,
      confidenceLevel: 0,
      learningStyle: 'balanced' // visual, kinesthetic, auditory, balanced
    };
    
    this.adaptations = {
      difficulty: 'medium',
      hintFrequency: 'normal',
      explanationDepth: 'standard',
      pacing: 'normal'
    };
    
    this.sessionData = {
      startTime: null,
      interactions: [],
      adaptationHistory: []
    };
  }

  /**
   * Initialize neuro-adaptive tracking
   */
  async initialize(userId, options = {}) {
    try {
      this.userId = userId;
      this.options = {
        trackTyping: true,
        trackFocus: true,
        trackMouse: true,
        privacyMode: true,
        ...options
      };
      
      this.isActive = true;
      this.sessionData.startTime = Date.now();
      
      // Start tracking
      if (this.options.trackTyping) {
        this.startTypingTracking();
      }
      
      if (this.options.trackFocus) {
        this.startFocusTracking();
      }
      
      if (this.options.trackMouse) {
        this.startMouseTracking();
      }
      
      console.log('🧠 Neuro-Adaptive Learning: ACTIVE');
      return { success: true };
    } catch (error) {
      console.error('Error initializing neuro-adaptive:', error);
      return { success: false, error };
    }
  }

  /**
   * Track typing patterns to detect confidence and understanding
   */
  startTypingTracking() {
    let lastKeyTime = Date.now();
    let consecutiveFastKeys = 0;
    let consecutiveSlowKeys = 0;
    
    document.addEventListener('keydown', (e) => {
      if (!this.isActive) return;
      
      const currentTime = Date.now();
      const timeSinceLastKey = currentTime - lastKeyTime;
      
      this.metrics.typingSpeed.push(timeSinceLastKey);
      
      // Fast typing (< 150ms) = confident
      if (timeSinceLastKey < 150) {
        consecutiveFastKeys++;
        consecutiveSlowKeys = 0;
        
        if (consecutiveFastKeys > 10) {
          this.recordInsight('confident_typing', {
            speed: 'fast',
            confidence: 'high'
          });
        }
      }
      
      // Slow typing (> 1000ms) = thinking/confused
      if (timeSinceLastKey > 1000) {
        consecutiveSlowKeys++;
        consecutiveFastKeys = 0;
        
        if (consecutiveSlowKeys > 3) {
          this.recordInsight('hesitant_typing', {
            speed: 'slow',
            confidence: 'low',
            suggestion: 'offer_hint'
          });
        }
      }
      
      // Backspace = making mistakes
      if (e.key === 'Backspace') {
        this.metrics.errorPatterns.push({
          time: currentTime,
          type: 'deletion'
        });
        
        // Multiple backspaces = struggling
        const recentErrors = this.metrics.errorPatterns.filter(
          err => currentTime - err.time < 5000
        );
        
        if (recentErrors.length > 5) {
          this.recordInsight('frequent_errors', {
            errorCount: recentErrors.length,
            suggestion: 'show_example'
          });
        }
      }
      
      lastKeyTime = currentTime;
      
      // Keep only recent data (last 100 keystrokes)
      if (this.metrics.typingSpeed.length > 100) {
        this.metrics.typingSpeed = this.metrics.typingSpeed.slice(-100);
      }
    });
  }

  /**
   * Track focus and attention using page visibility and scroll
   */
  startFocusTracking() {
    let focusStartTime = Date.now();
    let totalFocusTime = 0;
    
    // Page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!this.isActive) return;
      
      if (document.hidden) {
        // Lost focus
        const focusDuration = Date.now() - focusStartTime;
        totalFocusTime += focusDuration;
        
        this.recordInsight('lost_focus', {
          duration: focusDuration,
          totalFocus: totalFocusTime
        });
      } else {
        // Gained focus
        focusStartTime = Date.now();
        
        this.recordInsight('gained_focus', {
          timestamp: Date.now()
        });
      }
    });
    
    // Long periods without interaction = disengaged
    let lastInteractionTime = Date.now();
    
    const checkEngagement = () => {
      if (!this.isActive) return;
      
      const timeSinceInteraction = Date.now() - lastInteractionTime;
      
      // No interaction for 30 seconds = disengaged
      if (timeSinceInteraction > 30000) {
        this.recordInsight('disengaged', {
          idleTime: timeSinceInteraction,
          suggestion: 'offer_break_or_easier_content'
        });
      }
    };
    
    // Track any interaction
    ['click', 'keydown', 'scroll', 'mousemove'].forEach(event => {
      document.addEventListener(event, () => {
        if (!this.isActive) return;
        lastInteractionTime = Date.now();
      }, { passive: true });
    });
    
    // Check engagement every 10 seconds
    setInterval(checkEngagement, 10000);
  }

  /**
   * Track mouse movements to detect confusion or certainty
   */
  startMouseTracking() {
    let mouseMovements = [];
    let hoveredElements = new Set();
    
    document.addEventListener('mousemove', (e) => {
      if (!this.isActive) return;
      
      mouseMovements.push({
        x: e.clientX,
        y: e.clientY,
        time: Date.now()
      });
      
      // Keep only recent movements
      if (mouseMovements.length > 50) {
        mouseMovements = mouseMovements.slice(-50);
      }
      
      // Detect erratic movement (confusion)
      if (mouseMovements.length >= 10) {
        const recentMoves = mouseMovements.slice(-10);
        const distances = recentMoves.map((move, i) => {
          if (i === 0) return 0;
          const prev = recentMoves[i - 1];
          return Math.sqrt(
            Math.pow(move.x - prev.x, 2) + 
            Math.pow(move.y - prev.y, 2)
          );
        });
        
        const avgDistance = distances.reduce((a, b) => a + b, 0) / distances.length;
        
        // High movement = confused/searching
        if (avgDistance > 100) {
          this.recordInsight('erratic_mouse', {
            avgDistance,
            suggestion: 'highlight_key_elements'
          });
        }
      }
    }, { passive: true });
    
    // Track hovering (reading/contemplating)
    document.addEventListener('mouseover', (e) => {
      if (!this.isActive) return;
      
      const element = e.target;
      if (element.classList.contains('code-block') || 
          element.classList.contains('explanation')) {
        
        hoveredElements.add(element);
        
        setTimeout(() => {
          if (hoveredElements.has(element)) {
            // Still hovering after 3 seconds = reading carefully
            this.recordInsight('careful_reading', {
              element: element.className,
              suggestion: 'student_engaged'
            });
          }
        }, 3000);
      }
    }, { passive: true });
    
    document.addEventListener('mouseout', (e) => {
      hoveredElements.delete(e.target);
    }, { passive: true });
  }

  /**
   * Record behavioral insight
   */
  recordInsight(type, data) {
    const insight = {
      type,
      data,
      timestamp: Date.now(),
      userId: this.userId
    };
    
    this.sessionData.interactions.push(insight);
    
    // Trigger adaptation based on insight
    this.adaptLearning(insight);
    
    console.log('🧠 Insight:', type, data);
  }

  /**
   * Adapt learning experience based on insights
   */
  adaptLearning(insight) {
    const { type } = insight;
    let adaptation = null;
    
    switch (type) {
      case 'hesitant_typing':
        // Student is struggling - offer help
        adaptation = {
          action: 'show_hint',
          reason: 'Detected slow typing - student may need guidance',
          difficulty: 'decrease'
        };
        this.adaptations.hintFrequency = 'high';
        break;
        
      case 'confident_typing':
        // Student is confident - increase difficulty
        adaptation = {
          action: 'increase_difficulty',
          reason: 'Fast typing indicates confidence',
          difficulty: 'increase'
        };
        this.adaptations.difficulty = 'hard';
        break;
        
      case 'frequent_errors':
        // Many mistakes - provide example
        adaptation = {
          action: 'show_example',
          reason: 'Multiple errors detected',
          showVideo: true
        };
        this.adaptations.explanationDepth = 'detailed';
        break;
        
      case 'disengaged':
        // Student is bored or stuck
        adaptation = {
          action: 'change_activity',
          reason: 'Long idle period detected',
          suggestions: ['take_break', 'skip_ahead', 'watch_video']
        };
        break;
        
      case 'careful_reading':
        // Student is engaged and learning
        adaptation = {
          action: 'continue',
          reason: 'Student is carefully reading content',
          encouragement: true
        };
        break;
        
      case 'erratic_mouse':
        // Student is confused
        adaptation = {
          action: 'clarify',
          reason: 'Mouse movements indicate confusion',
          highlight: true
        };
        break;
      default:
        // Unknown insight type
        break;
    }
    
    if (adaptation) {
      this.sessionData.adaptationHistory.push({
        ...adaptation,
        timestamp: Date.now()
      });
      
      // Emit adaptation event
      this.emitAdaptation(adaptation);
    }
  }

  /**
   * Emit adaptation event for UI to respond
   */
  emitAdaptation(adaptation) {
    const event = new CustomEvent('neuro-adaptation', {
      detail: adaptation
    });
    
    document.dispatchEvent(event);
  }

  /**
   * Get current adaptations for UI
   */
  getCurrentAdaptations() {
    return {
      ...this.adaptations,
      recentInsights: this.sessionData.interactions.slice(-10)
    };
  }

  /**
   * Calculate confidence score (0-100)
   */
  calculateConfidence() {
    const recentTyping = this.metrics.typingSpeed.slice(-20);
    if (recentTyping.length === 0) return 50;
    
    const avgSpeed = recentTyping.reduce((a, b) => a + b, 0) / recentTyping.length;
    
    // Fast typing (< 200ms) = confident (80-100)
    // Medium typing (200-500ms) = normal (50-80)
    // Slow typing (> 500ms) = uncertain (0-50)
    
    let confidence = 0;
    if (avgSpeed < 200) {
      confidence = 80 + (200 - avgSpeed) / 10;
    } else if (avgSpeed < 500) {
      confidence = 50 + (500 - avgSpeed) / 10;
    } else {
      confidence = Math.max(0, 50 - (avgSpeed - 500) / 20);
    }
    
    this.metrics.confidenceLevel = Math.min(100, Math.max(0, confidence));
    return this.metrics.confidenceLevel;
  }

  /**
   * Get learning recommendations
   */
  getRecommendations() {
    const confidence = this.calculateConfidence();
    const recentErrors = this.metrics.errorPatterns.filter(
      err => Date.now() - err.time < 60000
    ).length;
    
    const recommendations = [];
    
    if (confidence < 30) {
      recommendations.push({
        type: 'support',
        message: 'Need a hint? You\'re doing great - let me help!',
        action: 'show_hint'
      });
    }
    
    if (confidence > 80) {
      recommendations.push({
        type: 'challenge',
        message: 'You\'re crushing it! Ready for a harder challenge?',
        action: 'increase_difficulty'
      });
    }
    
    if (recentErrors > 5) {
      recommendations.push({
        type: 'example',
        message: 'Let me show you an example to clarify!',
        action: 'show_example'
      });
    }
    
    return recommendations;
  }

  /**
   * Get session analytics
   */
  getAnalytics() {
    const sessionDuration = Date.now() - this.sessionData.startTime;
    
    return {
      duration: sessionDuration,
      interactions: this.sessionData.interactions.length,
      adaptations: this.sessionData.adaptationHistory.length,
      confidenceScore: this.calculateConfidence(),
      metrics: this.metrics,
      recommendations: this.getRecommendations()
    };
  }

  /**
   * Stop tracking
   */
  stop() {
    this.isActive = false;
    console.log('🧠 Neuro-Adaptive Learning: STOPPED');
  }

  /**
   * Privacy: Clear all data
   */
  clearData() {
    this.metrics = {
      typingSpeed: [],
      pauseDurations: [],
      errorPatterns: [],
      focusScore: 0,
      confidenceLevel: 0,
      learningStyle: 'balanced'
    };
    
    this.sessionData = {
      startTime: null,
      interactions: [],
      adaptationHistory: []
    };
    
    console.log('🧠 All data cleared (privacy mode)');
  }
}

const neuroAdaptiveService = new NeuroAdaptiveService();
export default neuroAdaptiveService;
